import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Toggle from '@atlaskit/toggle';

import { SettingCard } from './SettingCard.tsx';
import type { ApprovalMode, RecommendationType } from './recommendationType.ts';

/**
 * Placeholder seed list of recommendation types. Lives at module scope so the
 * reference is stable and we don't re-create the array on every render. Will be
 * replaced by data from a server fetch in a future change.
 */
const SEED_TYPES: RecommendationType[] = [
	{
		id: 'group-related-pages',
		title: 'Group related pages',
		description: 'Create a new sub-folder for a clear content cluster',
		enabled: true,
		approval: 'review',
		triggers: ['Daily at 9am'],
	},
	{
		id: 'rename-for-consistency',
		title: 'Rename for consistency',
		description: 'Recommend better names for pages and folders',
		enabled: true,
		approval: 'review',
		triggers: [],
	},
	{
		id: 'auto-linking',
		title: 'Auto linking',
		description: 'Finds related pages in the space, links them together',
		enabled: true,
		approval: 'review',
		triggers: [],
	},
];

export function SettingsView(): React.JSX.Element {
	const { formatMessage } = useIntl();

	// TODO(DISCO): replace local state with a REST/GraphQL-backed hook when the
	// settings API is available.
	const [enableSuggestions, setEnableSuggestions] = useState<boolean>(true);
	const [recommendationTypes, setRecommendationTypes] = useState<RecommendationType[]>(SEED_TYPES);

	const handleToggleEnableSuggestions = useCallback(() => {
		setEnableSuggestions((prev) => !prev);
	}, []);

	const handleToggleType = useCallback((id: string) => {
		setRecommendationTypes((prev) =>
			prev.map((type) => (type.id === id ? { ...type, enabled: !type.enabled } : type)),
		);
	}, []);

	const handleSetApproval = useCallback((id: string, mode: ApprovalMode) => {
		setRecommendationTypes((prev) =>
			prev.map((type) => (type.id === id ? { ...type, approval: mode } : type)),
		);
	}, []);

	// TODO(DISCO): wire to the real trigger creation flow when available.
	const handleAddTrigger = useCallback((_id: string) => {
		// No-op placeholder.
	}, []);

	return (
		<Stack space="space.300" testId="space-suggestions-settings">
			<Box>
				<Inline alignBlock="start" spread="space-between" space="space.200">
					<Stack space="space.050">
						<Text weight="bold">{formatMessage(i18n.enableSuggestionsLabel)}</Text>
						<Text size="small" color="color.text.subtlest">
							{formatMessage(i18n.enableSuggestionsDescription)}
						</Text>
					</Stack>
					<Toggle
						isChecked={enableSuggestions}
						onChange={handleToggleEnableSuggestions}
						label={formatMessage(i18n.enableSuggestionsLabel)}
						testId="space-suggestions-settings-enable-toggle"
					/>
				</Inline>
			</Box>

			<Stack space="space.150">
				<Box xcss={styles.sectionHeading}>
					<Heading size="small">{formatMessage(i18n.smartRecommendationTypesHeading)}</Heading>
				</Box>
				<Stack space="space.100">
					{recommendationTypes.map((type) => (
						<SettingCard
							key={type.id}
							type={type}
							onToggle={handleToggleType}
							onSetApproval={handleSetApproval}
							onAddTrigger={handleAddTrigger}
						/>
					))}
				</Stack>
			</Stack>
		</Stack>
	);
}

const styles = cssMap({
	sectionHeading: {
		marginBlockEnd: token('space.050'),
	},
});

const i18n = defineMessages({
	enableSuggestionsLabel: {
		id: 'space-suggestions.settings.enable-suggestions-label',
		defaultMessage: 'Enable suggestions',
		description:
			'Label for the master toggle that enables or disables all Knowledge curator suggestions for the space.',
	},
	enableSuggestionsDescription: {
		id: 'space-suggestions.settings.enable-suggestions-description',
		defaultMessage:
			'Choose whether you want smart recommendations and agent activity to be visible',
		description: 'Helper text under the "Enable suggestions" toggle explaining what it controls.',
	},
	smartRecommendationTypesHeading: {
		id: 'space-suggestions.settings.smart-recommendation-types-heading',
		defaultMessage: 'Smart recommendation types',
		description: 'Section heading for the list of configurable recommendation types.',
	},
});
